import React, { MouseEvent } from 'react';
import styled from 'styled-components';

export interface ActionButtonProps {
  text: string;
  onClick: (event?: MouseEvent<HTMLDivElement>) => void;
  className?: string;
  'data-cy'?: string;
  variant?: 'primary' | 'default';
  disabled?: boolean;
}

const ButtonBase = styled.div<{
  variant: 'primary' | 'default';
  disabled: boolean;
}>`
  align-self: stretch;
  border-radius: 8px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 0px 8px;
  cursor: ${props => (props.disabled ? 'not-allowed' : 'pointer')};
  transition: all 0.2s ease;
  opacity: ${props => (props.disabled ? 0.6 : 1)};

  background-color: ${props => {
    if (props.variant === 'primary') {
      return '#00779f';
    }
    if (props.variant === 'default') {
      return '#fff';
    }
    return '#f5f5f5';
  }};

  color: ${props => {
    if (props.variant === 'primary') {
      return '#fff';
    }
    return '#666';
  }};

  border: ${props => {
    if (props.variant === 'default') {
      return '1px solid #d9d9d9';
    }
    return 'none';
  }};

  &:hover {
    ${props =>
      !props.disabled &&
      `
      ${
        props.variant === 'primary'
          ? `
        background-color: #005d7a;
      `
          : `
        background-color: #e8e8e8;
        border-color: #bfbfbf;
      `
      }
    `}
  }
`;

const Content = styled.div`
  height: 32px;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
`;

const ButtonText = styled.div`
  position: relative;
  line-height: 24px;
  font-weight: 600;
  font-size: 14px;
`;

const ActionButton: React.FC<ActionButtonProps> = ({
  text,
  onClick,
  className,
  'data-cy': dataCy,
  variant = 'primary',
  disabled = false,
}) => {
  const handleClick = (e: MouseEvent<HTMLDivElement>) => {
    if (!disabled) {
      onClick(e);
    }
  };

  return (
    <ButtonBase
      className={className}
      onClick={handleClick}
      data-cy={dataCy}
      variant={variant}
      disabled={disabled}
    >
      <Content>
        <ButtonText>{text}</ButtonText>
      </Content>
    </ButtonBase>
  );
};

export default ActionButton;
